import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, Card, CardContent, Button, Box, Grid,
  LinearProgress, List, ListItem, ListItemText, Chip, Snackbar, Alert, 
  FormControl, InputLabel, MenuItem, Select, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  TextField
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import LanguageIcon from '@mui/icons-material/Language';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatFileSize, formatDate } from "../utils";
import HistoryTable from "../components/HistoryTable";
import * as api from "../services/api";

const Text2VoiceView = ({ latestWebSocketMessage, isConnected }) => {
  const ENGINE_OPTIONS = [
    { id: 'cosyvoice', label: 'CosyVoice 3 (Metal) — prueba velocidad' },
    { id: 'chatterbox', label: 'Chatterbox — audiolibro (LibriVox)' },
    { id: 'qwen', label: 'Qwen3-TTS (Metal) — audiolibro' },
    { id: 'xtts', label: 'XTTS / anterior' }
  ];

  const INSTRUCT_PRESETS = {
    audiolibro_es:
      'Calm, steady Spanish audiobook narration. Clear diction, natural pauses at commas and periods, warm and consistent, not theatrical, not like a voice assistant.',
    documental_es:
      'Spanish documentary narration. Measured pace, serious and clear, slight gravity, no acting.',
    cuento_es:
      'Spanish storytelling for a novel. Warm, intimate, slight expression on dialogue, never cartoonish.',
    audiolibro_en:
      'Calm, steady audiobook narration. Clear diction, natural pauses at commas and periods, warm and consistent, not theatrical, not like a voice assistant.',
    neutro: ''
  };

  const SPEAKER_OPTIONS = [
    { id: 'ryan', label: 'Ryan — audiolibro (recomendado)' },
    { id: 'aiden', label: 'Aiden — narrador claro' },
    { id: 'uncle_fu', label: 'Uncle Fu — grave / documental' },
    { id: 'serena', label: 'Serena — mujer cálida' },
    { id: 'dylan', label: 'Dylan — joven' },
    { id: 'eric', label: 'Eric — con carácter' },
    { id: 'vivian', label: 'Vivian — mujer brillante' },
    { id: 'sohee', label: 'Sohee — mujer expresiva' },
  ];

  // Chatterbox voice presets (ref WAVs under backend/text2voice/voices/chatterbox/)
  const CHATTERBOX_SPEAKER_OPTIONS = [
    { id: 'librivox_hombre', label: 'Narrador hombre (LibriVox) — audiolibro' },
    { id: 'librivox_mujer', label: 'Narradora Karen Savage (LibriVox)' },
    { id: 'librivox_clasico', label: 'Narración clásica (Quijote / LibriVox)' },
    { id: 'librivox_suave', label: 'Narración suave (Platero / LibriVox)' },
    { id: 'hombre_dinamico', label: 'Legacy: Rocko MX (say)' },
    { id: 'hombre_grave', label: 'Legacy: Grandpa MX (say)' },
    { id: 'hombre_claro', label: 'Legacy: Eddy MX (say)' },
    { id: 'mujer', label: 'Legacy: Paulina (say)' },
  ];

  const engineLabel = (id) => {
    const found = ENGINE_OPTIONS.find((e) => e.id === id);
    return found ? found.label : (id || 'qwen');
  };

  const engineChipColor = (id) => {
    if (id === 'qwen') return 'success';
    if (id === 'chatterbox') return 'secondary';
    if (id === 'cosyvoice') return 'warning';
    return 'default';
  };

  const [epubFile, setEpubFile] = useState(null);
  const [epubResult, setEpubResult] = useState(null);
  const [epubLoading, setEpubLoading] = useState(false);
  const [textFile, setTextFile] = useState(null);
  const [language, setLanguage] = useState('es');
  const [engine, setEngine] = useState('cosyvoice');
  const [speaker, setSpeaker] = useState('librivox_hombre');
  const [instructPreset, setInstructPreset] = useState('audiolibro');
  const [instruct, setInstruct] = useState(INSTRUCT_PRESETS.audiolibro_es);
  const [voiceQueue, setVoiceQueue] = useState([]);
  const [voiceProgress, setVoiceProgress] = useState({});
  const [voiceHistory, setVoiceHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [playingFilename, setPlayingFilename] = useState(null);
  const audioRef = useRef(null);
  const playingFilenameRef = useRef(null);

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audioRef.current = null;
    }
    playingFilenameRef.current = null;
    setPlayingFilename(null);
  }, []);

  const handleTogglePlay = useCallback((filename) => {
    if (!filename) return;
    const audio = audioRef.current;
    if (audio && playingFilenameRef.current === filename) {
      if (audio.paused) {
        audio.play().catch((err) => {
          console.error("Error reproduciendo audio:", err);
          setError("Error al reproducir el audio. Intente descargarlo.");
        });
        setPlayingFilename(filename);
      } else {
        audio.pause();
        setPlayingFilename(null);
      }
      return;
    }

    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }

    const next = new Audio(api.getText2VoiceDownloadUrl(filename));
    audioRef.current = next;
    playingFilenameRef.current = filename;
    next.onended = () => {
      if (playingFilenameRef.current === filename) {
        playingFilenameRef.current = null;
        setPlayingFilename(null);
      }
    };
    next.play().then(() => {
      setPlayingFilename(filename);
    }).catch((err) => {
      console.error("Error reproduciendo audio:", err);
      setError("Error al reproducir el audio. Intente descargarlo.");
      stopPlayback();
    });
  }, [stopPlayback]);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  // Función para buscar la cola de conversión
  const fetchText2VoiceQueue = useCallback(async () => {
    try {
      const response = await api.fetchText2VoiceQueue();
      setVoiceQueue(response);
      const nextProgress = {};
      (response || []).forEach((item) => {
        if (item.progress) nextProgress[item.filename] = item.progress;
      });
      if (Object.keys(nextProgress).length) {
        setVoiceProgress((prev) => ({ ...prev, ...nextProgress }));
      }
    } catch (err) {
      console.error("Error al obtener cola de conversión:", err);
      setError("Error al cargar la cola de conversión de texto a voz");
    }
  }, []);

  // Función para buscar el historial de conversiones
  const fetchText2VoiceHistory = useCallback(async () => {
    try {
      const response = await api.fetchText2VoiceHistory();
      console.log('Datos del historial recibidos:', response);
      setVoiceHistory(response);
    } catch (err) {
      console.error("Error al obtener historial de conversión:", err);
      setError("Error al cargar el historial de conversión de texto a voz");
    }
  }, []);

  // Carga inicial de datos
  useEffect(() => {
    console.log("Cargando datos iniciales...");
    fetchText2VoiceQueue();
    fetchText2VoiceHistory();
    
    // Configura un intervalo para actualizar la cola si no hay WebSockets
    const intervalId = setInterval(() => {
      fetchText2VoiceQueue();
      fetchText2VoiceHistory(); // También actualizar periódicamente el historial
    }, 10000); // Cada 10 segundos
    
    return () => clearInterval(intervalId);
  }, [fetchText2VoiceQueue, fetchText2VoiceHistory]);

  // Responder a mensajes WebSocket
  useEffect(() => {
    if (latestWebSocketMessage) {
      console.log("WebSocket message in Text2Voice:", latestWebSocketMessage);
      if (latestWebSocketMessage.type === 'text2voiceProgress') {
        setVoiceProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: latestWebSocketMessage.data
        }));
      } else if (latestWebSocketMessage.type === 'text2voiceQueueUpdate') {
        setVoiceQueue(latestWebSocketMessage.queue);
      } else if (latestWebSocketMessage.type === 'text2voiceComplete') {
        console.log('Conversión completada, mensaje completo:', latestWebSocketMessage);
        setVoiceProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: { 
            ...prev[latestWebSocketMessage.filename],
            completed: true 
          }
        }));
        
        // Forzar actualización del historial después de un breve retraso
        setTimeout(() => {
          console.log('Actualizando historial después de conversión completada');
          fetchText2VoiceHistory();
        }, 1000);
        
        fetchText2VoiceQueue();
        setSuccessMessage(`Conversión de texto a voz completada para ${latestWebSocketMessage.originalFilename}`);
      } else if (latestWebSocketMessage.type === 'text2voiceError') {
        setError(`Error durante la conversión de ${latestWebSocketMessage.originalFilename}: ${latestWebSocketMessage.error}`);
      } else if (latestWebSocketMessage.type === 'text2voiceHistoryCleared') {
        fetchText2VoiceHistory();
        setSuccessMessage("Historial de conversiones limpiado correctamente");
      } else if (latestWebSocketMessage.type === 'text2voiceItemDeleted') {
        fetchText2VoiceHistory();
        setSuccessMessage("Archivo de conversión eliminado correctamente");
      }
    }
  }, [latestWebSocketMessage, fetchText2VoiceQueue, fetchText2VoiceHistory]);

  // Manejar el cambio de archivo de texto
  const handleTextFileChange = (e) => {
    setTextFile(e.target.files[0]);
    setError("");
    setSuccessMessage("");
  };

  const handleEpubFileChange = (e) => {
    setEpubFile(e.target.files[0] || null);
    setEpubResult(null);
    setError("");
    setSuccessMessage("");
  };

  const handleConvertEpub = async () => {
    if (!epubFile) {
      setError('Selecciona un archivo EPUB primero.');
      return;
    }
    setEpubLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const result = await api.convertEpubToTxt(epubFile);
      setEpubResult(result);
      setSuccessMessage(result.message || 'EPUB convertido a TXT.');
    } catch (err) {
      console.error('Error al convertir EPUB:', err);
      setError(err.response?.data?.error || 'No se pudo convertir el EPUB.');
    } finally {
      setEpubLoading(false);
    }
  };

  const handleUseConvertedTxt = async () => {
    if (!epubResult?.filename) return;
    try {
      const response = await fetch(api.getConvertedTxtUrl(epubResult.filename));
      if (!response.ok) throw new Error('No se pudo descargar el TXT');
      const blob = await response.blob();
      const name = epubResult.filename.replace(/^\d+-[a-f0-9]+-/, '') || 'libro.txt';
      const file = new File([blob], name.endsWith('.txt') ? name : `${name}.txt`, { type: 'text/plain' });
      setTextFile(file);
      setSuccessMessage(`TXT listo para texto a voz: ${file.name}`);
    } catch (err) {
      console.error('Error al usar TXT convertido:', err);
      setError('No se pudo cargar el TXT para texto a voz.');
    }
  };

  // Manejar el cambio de idioma
  const defaultInstructFor = (lang, preset) => {
    if (preset === 'neutro') return INSTRUCT_PRESETS.neutro;
    if (preset === 'documental') return INSTRUCT_PRESETS.documental_es;
    if (preset === 'cuento') return INSTRUCT_PRESETS.cuento_es;
    return lang === 'en' ? INSTRUCT_PRESETS.audiolibro_en : INSTRUCT_PRESETS.audiolibro_es;
  };

  const handleLanguageChange = (e) => {
    const next = e.target.value;
    setLanguage(next);
    if (engine === 'qwen') {
      setInstruct(defaultInstructFor(next, instructPreset));
    }
  };

  const handleInstructPresetChange = (e) => {
    const next = e.target.value;
    setInstructPreset(next);
    setInstruct(defaultInstructFor(language, next));
  };

  const handleEngineChange = (e) => {
    const next = e.target.value;
    setEngine(next);
    if (next === 'qwen') {
      setSpeaker('ryan');
    } else if (next === 'chatterbox' || next === 'cosyvoice') {
      setSpeaker('librivox_hombre');
    }
  };

  const voiceOptionsForEngine = (eng) => {
    if (eng === 'chatterbox' || eng === 'cosyvoice') return CHATTERBOX_SPEAKER_OPTIONS;
    if (eng === 'qwen') return SPEAKER_OPTIONS;
    return [];
  };

  const voiceLabel = (eng, id) => {
    const opts = voiceOptionsForEngine(eng);
    return opts.find((s) => s.id === id)?.label || id || '—';
  };

  // Manejar el cambio de speaker Qwen
  const handleSpeakerChange = (e) => {
    setSpeaker(e.target.value);
  };

  // Manejar la subida y conversión
  const handleUploadAndConvert = async (e) => {
    e.preventDefault();
    if (!textFile) {
      setError("Por favor selecciona un archivo de texto primero.");
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await api.uploadTextForVoice(
        textFile,
        language,
        undefined,
        engine,
        (engine === 'qwen' || engine === 'chatterbox' || engine === 'cosyvoice') ? speaker : undefined,
        engine === 'qwen' ? instruct : undefined
      );
      setSuccessMessage(response.message || "Archivo subido y conversión iniciada.");
      setTextFile(null); // Limpiar el input
      fetchText2VoiceQueue(); // Refrescar la cola
    } catch (err) {
      console.error("Error al subir texto:", err);
      setError(err.response?.data?.error || "Error al subir el archivo de texto.");
    } finally {
      setIsLoading(false);
    }
  };

  // Función para limpiar el historial de conversión
  const handleClearText2VoiceHistory = async () => {
    try {
      stopPlayback();
      await api.clearText2VoiceHistory();
      fetchText2VoiceHistory();
      setSuccessMessage("Historial de conversiones limpiado correctamente");
    } catch (error) {
      console.error('Error al limpiar historial de conversión:', error);
      setError("Error al limpiar el historial de conversión");
    }
  };

  const handleRetryText2Voice = async (item) => {
    try {
      await api.retryText2VoiceItem(item.filename);
      fetchText2VoiceQueue();
      setSuccessMessage(`Reanudando ${item.originalFilename} desde el último capítulo guardado.`);
    } catch (err) {
      console.error('Error al reintentar TTS:', err);
      setError(err.response?.data?.error || 'No se pudo reintentar la conversión.');
    }
  };

  // Función para eliminar un archivo específico de conversión
  const handleDeleteText2VoiceItem = async (item) => {
    try {
      if (playingFilenameRef.current === item.processedFilename) {
        stopPlayback();
      }
      await api.deleteText2VoiceItem(item.processedFilename);
      fetchText2VoiceHistory();
      setSuccessMessage(`Conversión de ${item.originalFilename} eliminada correctamente`);
    } catch (error) {
      console.error('Error al eliminar conversión:', error);
      setError("Error al eliminar la conversión");
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h5" component="h1" gutterBottom>
        Convertir Texto a Voz
      </Typography>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Conectando al servidor WebSocket...
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Convertir EPUB a TXT</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Extrae el texto del libro con títulos de capítulo para que el TTS lo parta por secciones.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<MenuBookIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  Seleccionar EPUB
                  <input type="file" accept=".epub,application/epub+zip" hidden onChange={handleEpubFileChange} />
                </Button>
                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {epubFile ? `${epubFile.name} (${formatFileSize(epubFile.size)})` : 'Archivo .epub'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                fullWidth
                disabled={!epubFile || epubLoading}
                onClick={handleConvertEpub}
                startIcon={<MenuBookIcon />}
              >
                {epubLoading ? 'Convirtiendo…' : 'Convertir a TXT'}
              </Button>
            </Grid>
            {epubResult && (
              <>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(api.getConvertedTxtUrl(epubResult.filename), '_blank')}
                  >
                    Descargar
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button variant="contained" color="secondary" fullWidth onClick={handleUseConvertedTxt}>
                    Usar para TTS
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {epubResult.chapters} capítulos · {epubResult.chars.toLocaleString()} caracteres
                    {epubResult.chapterTitles?.length
                      ? ` · ${epubResult.chapterTitles.slice(0, 8).join(' · ')}${epubResult.chapterTitles.length > 8 ? '…' : ''}`
                      : ''}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Sección de Subida */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Subir Archivo de Texto</Typography>
          <form onSubmit={handleUploadAndConvert}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4} lg={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    Seleccionar Archivo
                    <input
                      type="file"
                      accept=".txt,.md,.text"
                      hidden
                      onChange={handleTextFileChange}
                    />
                  </Button>
                  <Typography variant="body2" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {textFile 
                      ? `${textFile.name} (${formatFileSize(textFile.size)})`
                      : 'Formatos: .txt, .md, .text'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <FormControl fullWidth>
                  <InputLabel id="language-select-label">Idioma</InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={language}
                    label="Idioma"
                    onChange={handleLanguageChange}
                    startAdornment={<LanguageIcon sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">Inglés</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <FormControl fullWidth>
                  <InputLabel id="engine-select-label">Motor TTS</InputLabel>
                  <Select
                    labelId="engine-select-label"
                    id="engine-select"
                    value={engine}
                    label="Motor TTS"
                    onChange={handleEngineChange}
                    startAdornment={<RecordVoiceOverIcon sx={{ mr: 1 }} />}
                  >
                    {ENGINE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {(engine === 'qwen' || engine === 'chatterbox' || engine === 'cosyvoice') && (
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormControl fullWidth>
                    <InputLabel id="speaker-select-label">Voz</InputLabel>
                    <Select
                      labelId="speaker-select-label"
                      id="speaker-select"
                      value={speaker}
                      label="Voz"
                      onChange={handleSpeakerChange}
                    >
                      {voiceOptionsForEngine(engine).map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {engine === 'qwen'
                      ? 'Ryan + instruct de audiolibro (1.7B). El sample WAV no se usa.'
                      : engine === 'cosyvoice'
                        ? 'Mismas refs LibriVox que Chatterbox. CosyVoice 3 en Metal; textos ~800 caracteres.'
                        : 'Default: narrador LibriVox humano. Textos largos se parten (~450 caracteres).'}
                  </Typography>
                </Grid>
              )}
              {engine === 'qwen' && (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="instruct-preset-label">Estilo</InputLabel>
                      <Select
                        labelId="instruct-preset-label"
                        value={instructPreset}
                        label="Estilo"
                        onChange={handleInstructPresetChange}
                      >
                        <MenuItem value="audiolibro">Audiolibro (recomendado)</MenuItem>
                        <MenuItem value="documental">Documental</MenuItem>
                        <MenuItem value="cuento">Cuento / novela</MenuItem>
                        <MenuItem value="neutro">Natural (sin instruct)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Instruct (estilo de lectura)"
                      value={instruct}
                      onChange={(e) => setInstruct(e.target.value)}
                      helperText="El 1.7B sigue este estilo. Textos largos se parten por párrafos (~2200 caracteres) y se unen con una pausa."
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={(engine === 'qwen' || engine === 'chatterbox' || engine === 'cosyvoice') ? 12 : 3} lg={(engine === 'qwen' || engine === 'chatterbox' || engine === 'cosyvoice') ? 12 : 3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!textFile || isLoading}
                  startIcon={<RecordVoiceOverIcon />}
                >
                  Convertir a Voz
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Cola de Procesamiento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Cola de Conversión</Typography>
          {voiceQueue.length === 0 ? (
            <Typography variant="body1">No hay archivos en la cola de conversión.</Typography>
          ) : (
            <List>
              {voiceQueue.map((item) => (
                <ListItem key={item.id} sx={{ 
                  borderBottom: '1px solid #e0e0e0',
                  '&:last-child': { borderBottom: 'none' }
                }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <ListItemText 
                        primary={item.originalFilename} 
                        secondary={`Añadido: ${formatDate(item.addedDate)}`}
                      />
                      <Chip 
                        label={item.language === 'es' ? 'Español' : 'Inglés'} 
                        color="info" 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                      <Chip 
                        label={engineLabel(item.engine)} 
                        color={engineChipColor(item.engine)} 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                      {item.speaker && (item.engine === 'qwen' || item.engine === 'chatterbox' || item.engine === 'cosyvoice') && (
                        <Chip
                          label={voiceLabel(item.engine, item.speaker)}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                      <Chip 
                        label={item.status} 
                        color={item.status === 'Processing' ? 'primary' : 'default'} 
                        variant={item.status === 'Queued' ? 'outlined' : 'filled'}
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    {(item.processing || item.status === 'Processing' || item.status === 'Error' || voiceProgress[item.filename]) && (
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={voiceProgress[item.filename]?.progress || 0} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" display="block" textAlign="center" mt={0.5}>
                          {voiceProgress[item.filename]?.message
                            || (item.status === 'Error' ? 'Error — se puede reanudar' : 'En cola…')}
                        </Typography>
                        {voiceProgress[item.filename]?.totalSections > 0 && (
                          <Typography variant="caption" display="block" textAlign="center" color="text.secondary">
                            {voiceProgress[item.filename].completedSections || 0}/{voiceProgress[item.filename].totalSections} secciones
                          </Typography>
                        )}
                        {Array.isArray(voiceProgress[item.filename]?.sections) && voiceProgress[item.filename].sections.length > 1 && (
                          <Box sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                            {voiceProgress[item.filename].sections.map((section) => (
                              <Typography key={section.id || section.index} variant="caption" display="block" color="text.secondary">
                                {section.status === 'completed' ? '✓' : section.status === 'processing' ? '►' : section.status === 'error' ? '✗' : section.status === 'skipped' ? '–' : '·'}{' '}
                                {section.title}
                                {section.chunksTotal ? ` (${section.chunksDone || 0}/${section.chunksTotal})` : ''}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {item.status === 'Error' && (
                          <Button size="small" sx={{ mt: 1 }} onClick={() => handleRetryText2Voice(item)}>
                            Reanudar
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Historial de Conversiones */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Historial de Conversiones</Typography>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={handleClearText2VoiceHistory}
              disabled={voiceHistory.length === 0}
            >
              Limpiar Historial
            </Button>
          </Box>
          
          {voiceHistory.length === 0 ? (
            <Typography variant="body1">No hay conversiones en el historial.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="tabla de historial de conversiones">
                <TableHead>
                  <TableRow>
                    <TableCell>Archivo Original</TableCell>
                    <TableCell>Audio Generado</TableCell>
                    <TableCell>Idioma</TableCell>
                    <TableCell>Motor</TableCell>
                    <TableCell>Voz</TableCell>
                    <TableCell>Fecha de Conversión</TableCell>
                    <TableCell align="center">Audio</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voiceHistory.map((item) => (
                    <TableRow key={item.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {item.originalFilename}
                      </TableCell>
                      <TableCell>{item.processedFilename}</TableCell>
                      <TableCell>{item.language === 'es' ? 'Español' : 'Inglés'}</TableCell>
                      <TableCell>
                        <Chip
                          label={engineLabel(item.engine)}
                          color={engineChipColor(item.engine)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(item.engine === 'qwen' || item.engine === 'chatterbox' || item.engine === 'cosyvoice')
                          ? voiceLabel(item.engine, item.speaker)
                          : '—'}
                      </TableCell>
                      <TableCell>{formatDate(item.completedDate)}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => window.open(api.getText2VoiceDownloadUrl(item.processedFilename), '_blank')}
                          sx={{ mr: 1 }}
                        >
                          Descargar
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={playingFilename === item.processedFilename ? <PauseIcon /> : <PlayArrowIcon />}
                          onClick={() => handleTogglePlay(item.processedFilename)}
                        >
                          {playingFilename === item.processedFilename ? 'Pausar' : 'Reproducir'}
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteText2VoiceItem(item)}
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Notificaciones */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError("")} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Text2VoiceView; 